// MealPlanner.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../axiosInstance";
import { marked } from "marked";
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Link,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import { AuthContext } from "../context/AuthContext";
import { ChatContext } from "../context/ChatContext";
import { ModalContext } from "../context/ModalContext";
import { SettingsContext } from "../context/SettingsContext";
import AuthDrawer from "./AuthDrawer";
import { getDomainFromUrl } from "../utils/getDomainFromUrl";
import chefLogo from "../assets/images/chefchattygloballarge.png";
import Typewriter from "./Typewriter";
import config from "../config.js";
import ShareRecipeButton from "./ShareRecipeButton";

// Utility to strip HTML tags from strings
const stripHtmlTags = (text) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = text;
  return tempDiv.textContent || tempDiv.innerText || "";
};

const MealPlanner = () => {
  const {
    messages,
    addMessage,
    savedRecipes,
    saveRecipe,
    resetConversationLocally,
    setMessageTyped,
    botName,
  } = useContext(ChatContext);

  const {
    dietaryRestrictions,
    foodAllergies,
    measurementUnits,
    chefPersona,
    chatTextSize,
  } = useContext(SettingsContext);

  const [input, setInput] = useState("");
  const [conversationHistory, setConversationHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const { openInfoModal, openConfirmModal } = useContext(ModalContext);
  const [openImagePopup, setOpenImagePopup] = useState(false);
  const [popupImageUrl, setPopupImageUrl] = useState(null);
  const [popupImageTitle, setPopupImageTitle] = useState(""); // store recipe name

  // Controls the Login/Signup modal visibility
  // const [showLoginModal, setShowLoginModal] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  // Auth context provides existing login and signup methods
  const {
    accessToken,
    login,
    signup,
    onRequestPasswordReset,
    onResendConfirmation,
  } = useContext(AuthContext);

  const { typewriterEffect } = useContext(SettingsContext);

  // We'll use React Router's navigate to push user to /check-email after signup
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const chatFontSize = config.fontSize[chatTextSize];

  // Scroll to bottom of messages whenever they change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle text input changes
  const handleInputChange = (e) => setInput(e.target.value);

  // Handler to open the dialog
  const handleImageClick = (imageUrl, recipeName) => {
    setPopupImageUrl(imageUrl);
    setPopupImageTitle(recipeName);
    setOpenImagePopup(true);
  };

  // Handler to close the dialog
  const handleCloseImagePopup = () => {
    setOpenImagePopup(false);
    setPopupImageUrl(null);
    setPopupImageTitle("");
  };

  // Handle conversation reset
  const resetConversation = async () => {
    try {
      await axiosInstance.post("/api/meal-plan/reset");
      resetConversationLocally();
      setInput("");
    } catch (error) {
      console.error("Error resetting conversation:", error);
    }
  };

  // Save a recipe to the user's account
  const handleSaveRecipe = async (recipeContent) => {
    if (!accessToken) {
      setShowDrawer(true);
      return;
    }
    try {
      const recipeExists = await axiosInstance.get(
        `/api/recipes/saved/${recipeContent.recipeName}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      console.log("recipeExists = ", recipeExists);
      if (recipeExists.data) {
        openConfirmModal({
          title: "Save Recipe?",
          message: `You have already saved a recipe for ${recipeContent.recipeName}. Do you want to save it again?`,
          onConfirm: async () => {
            await doSaveRecipe(recipeContent, true);
          },
        });
      } else {
        await doSaveRecipe(recipeContent, false);
      }
    } catch (error) {
      console.error("Error checking recipe existence:", error);
      openInfoModal({
        title: "Error",
        message: `Failed to check if recipe exists. Please try again.\n${error}`,
      });
    }
  };

  /**
   * doSaveRecipe: the actual POST to /api/recipes/save.
   * allowDuplicate param indicates user has confirmed saving a duplicate.
   */
  const doSaveRecipe = async (recipeContent, allowDuplicate) => {
    try {
      // Possibly pass allowDuplicate in request body or query param
      const response = await axiosInstance.post(
        "/api/recipes/save",
        { ...recipeContent, allowDuplicate },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      // If successful, show success info modal and store in chat context
      openInfoModal({
        title: "Saved",
        message: response.data.message,
        onClose: () => {
          // e.g. store in chat context or local state
          saveRecipe(recipeContent);
        },
      });
    } catch (error) {
      console.log("Error saving recipe:", error);

      // If server returns 409 or 400, handle accordingly
      if (error.response && error.response.status === 409) {
        openInfoModal({
          title: "Conflict",
          message:
            "This recipe has already been saved, and duplicates are not allowed. Please check your recipes.",
        });
      } else {
        openInfoModal({
          title: "Error",
          message: `Failed to save the recipe. Please try again.\n${error}`,
        });
      }
    }
  };

  // Custom handler for SIGNUP — wraps the AuthContext signup
  // and navigates to /check-email on success
  const handleSignup = async (
    firstName,
    lastName,
    username,
    email,
    password
  ) => {
    try {
      // Call the AuthContext signup method
      await signup(firstName, lastName, username, email, password);
      // Then navigate to check-email screen, passing user's email in state
      navigate("/check-email", { state: { email } });
    } catch (error) {
      // Rethrow or handle error here so the modal can show a message
      throw error;
    }
  };

  // Send a message to the meal-plan endpoint
  const sendMessage = async (message = input) => {
    if (!message) {
      return;
    }

    // Additional logic to handle "start over" commands
    if (message.toLowerCase().includes("start over")) {
      // local reset
      resetConversationLocally();
      setInput("");
      await axiosInstance.post("/api/meal-plan/reset");
      return;
    }

    // Add local user message to chat context
    addMessage("You", message);
    setInput("");
    setLoading(true);

    try {
      // Build the conversation history
      const assistantFriendlySummary = (msg) => {
        if (msg.tag === "recipe") {
          const { recipeName, introduction } = msg.text || {};
          return `Recipe shared: ${recipeName || "Unnamed dish"} – ${introduction || ""}`;
        }
        return msg.text;
      };
      
      // Manually include the most recent user message
      const allMessages = [...messages, { sender: "You", text: message }];
      
      const recentHistory = allMessages
        .slice(-6) // last 6 messages including the new user message
        .map((msg) => ({
          role: msg.sender === "You" ? "user" : "assistant",
          content:
            msg.sender === botName ? assistantFriendlySummary(msg) : msg.text,
        }));
      
      setConversationHistory(recentHistory);
      
      // Create the final request payload
      let initialPrompt = `You are ${chefPersona} who progressively guides users through cuisines, food types, and specific dish choices from around the world.`;
      initialPrompt += `You are to acknowledge and tailor responses to the user's preferences. 
      - Dietary restrictions: ${dietaryRestrictions}.
      - Allergies: ${foodAllergies}.
      - System of units: ${measurementUnits}.
      Please ensure all recipes follow these constraints.`;
      initialPrompt += `Generate recipes when the user's message includes a dish name or the user agrees to receive a recipe.`;
      // initialPrompt +=
      //   "Do not send recipe unless the user asks for it or types the name of a dish.";
      // initialPrompt += "Send only one recipe at a time";
      initialPrompt +=
        "Your responses should never mix normal text with a JSON.";
      initialPrompt += `
        If your response contains a recipe, your entire response (recipe + your acknowledgement of the user's message) must all be packaged in JSON of this structure:
        {
            "tag": "recipe",
            "content": {
                "recipeName": "Example Name",
                "conversation_response: "Your acknowledgement to the user's message",
                "introduction": "Two to three sentences summarizing the dish and the cultural context around the dish",
                "ingredients": ["ingredient1", "ingredient2"],
                "instructions": ["step1", "step2"],
                "tips": ["tip1", "tip2"],
                "friendly_closing": "Some encouraging message"
            }
        }`;
      initialPrompt +=
        "When sending a recipe, do not include explanations, greetings, or any text before or after the JSON.";
      initialPrompt +=
        "Send recipe for only one dish at a time. If the user asks for a multi-course meal prompt them for each dish.";
      initialPrompt += "Otherwise, respond normally.";
      initialPrompt += "Do not number the steps in recipe instructions.";

      const systemPrompt = { role: "system", content: initialPrompt };
      const requestPayload = [
        systemPrompt,
        ...recentHistory,
        // newUserMessage,
      ];

      const response = await axiosInstance.post("/api/meal-plan", {
        messages: requestPayload,
      });

      const { reply, tag } = response.data;

      console.log("reply: ", reply);

      // Add assistant's reply to chat
      addMessage(botName, reply, tag);
    } catch (error) {
      console.error("Error communicating with the backend:", error);
      addMessage(
        botName,
        "Sorry, I'm having trouble understanding right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Render a recipe message
  const renderRecipe = (recipeContent) => {
    // const { recipeName, imageUrl } = recipeContent;

    try {
      const {
        recipeName,
        introduction,
        ingredients,
        instructions,
        tips,
        friendly_closing,
        imageUrl,
      } = recipeContent;

      const isRecipeSaved = savedRecipes.includes(recipeContent.recipeId);

      return (
        <Box sx={{ fontSize: chatFontSize }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: 3,
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "inherit",
                fontWeight: "bold",
                marginBottom: 1,
                marginRight: 2,
              }}
            >
              {stripHtmlTags(recipeName)}
            </Typography>
            {/* Thumbnail next to dish name */}
            {imageUrl && (
              <img
                src={imageUrl}
                alt={recipeName}
                style={{
                  width: "80px",
                  height: "80px",
                  objectFit: "cover",
                  cursor: "pointer",
                  borderRadius: "8px",
                }}
                onClick={() => handleImageClick(imageUrl, recipeName)}
              />
            )}
          </Box>

          <Typography variant="body2" gutterBottom sx={{ fontSize: "inherit" }}>
            {stripHtmlTags(introduction)}
          </Typography>

          <Typography
            variant="body2"
            sx={{ fontSize: "inherit", fontWeight: "bold", marginTop: 2 }}
          >
            Ingredients
          </Typography>
          <ul
            style={{
              paddingLeft: "20px",
              marginTop: "4px",
              marginBottom: "8px",
            }}
          >
            {ingredients.map((ingredient, i) => (
              <li key={i}>
                <Typography sx={{ fontSize: "inherit" }} variant="body2">
                  {stripHtmlTags(ingredient)}
                </Typography>
              </li>
            ))}
          </ul>
          <Typography
            variant="body2"
            sx={{ fontSize: "inherit", fontWeight: "bold", marginTop: 2 }}
          >
            Instructions
          </Typography>
          <ol
            style={{
              paddingLeft: "20px",
              marginTop: "4px",
              marginBottom: "8px",
            }}
          >
            {Array.isArray(instructions) ? (
              instructions.map((step, i) => (
                <li key={i}>
                  <Typography sx={{ fontSize: "inherit" }} variant="body2">
                    {stripHtmlTags(step)}
                  </Typography>
                </li>
              ))
            ) : (
              <Typography variant="body2" sx={{ fontSize: "inherit" }}>
                {stripHtmlTags(instructions)}
              </Typography>
            )}
          </ol>
          {tips && (
            <>
              <Typography
                variant="body2"
                sx={{ fontSize: "inherit", fontWeight: "bold", marginTop: 2 }}
              >
                Tips
              </Typography>
              <ul
                style={{
                  paddingLeft: "20px",
                  marginTop: "4px",
                  marginBottom: "8px",
                }}
              >
                {tips.map((tip, i) => (
                  <li key={i}>
                    <Typography variant="body2" sx={{ fontSize: "inherit" }}>
                      {stripHtmlTags(tip)}
                    </Typography>
                  </li>
                ))}
              </ul>
            </>
          )}
          <Typography variant="body2" sx={{ fontSize: "inherit" }} gutterBottom>
            {stripHtmlTags(friendly_closing)}
          </Typography>
          {!isRecipeSaved ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<SaveIcon />}
              onClick={() => handleSaveRecipe(recipeContent)}
              aria-label="save"
            >
              Save
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              disabled
              startIcon={<CheckIcon />} // or a check icon
            >
              Saved
            </Button>
          )}
          <ShareRecipeButton
            recipeId={recipeContent.recipeId}
            recipeTitle={recipeContent.recipeName}
            recipeContent={recipeContent}
          />
        </Box>
      );
    } catch (error) {
      console.error("Error rendering recipe:", error);
      return (
        <Typography variant="body2">
          Error displaying the recipe. Please try again.
        </Typography>
      );
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="90vh">
      <Container maxWidth="sm" sx={{ flexGrow: 1, overflowY: "auto" }}>
        {/* <Typography variant="h4" align="center" gutterBottom>
        Chef Chatty
      </Typography> */}
        <Paper elevation={3} sx={{ padding: 2, marginBottom: 0 }}>
          <List sx={{ paddingBottom: 0 }}>
            {messages.map((message, index) => (
              <React.Fragment key={index}>
                <ListItem alignItems="flex-start">
                  <Avatar
                    sx={{
                      bgcolor:
                        message.sender === "You" ? "blue" : "transparent",
                    }}
                  >
                    {message.sender === "You" ? (
                      <PersonIcon />
                    ) : (
                      <img
                        src={chefLogo}
                        alt={botName}
                        style={{
                          height: "50px",
                          width: "50px",
                          cursor: "pointer",
                          border: "1px solid white",
                        }}
                      />
                    )}
                  </Avatar>
                  <ListItemText
                    primary={message.sender === "You" ? "You" : botName}
                    secondary={
                      message.tag === "recipe" ? (
                        renderRecipe(message.text)
                      ) : message.sender === botName &&
                        !message.typed &&
                        typewriterEffect ? (
                        <Typewriter
                          rawText={stripHtmlTags(message.text)}
                          speed={5}
                          style={{ fontSize: chatFontSize }}
                          onComplete={() => setMessageTyped(index)}
                          onProgress={() => {
                            messagesEndRef.current?.scrollIntoView({
                              behavior: "smooth",
                            });
                          }}
                        />
                      ) : (
                        <div
                          style={{ fontSize: chatFontSize }}
                          dangerouslySetInnerHTML={{
                            __html: marked.parse(stripHtmlTags(message.text)),
                          }}
                        />
                      )
                    }
                    secondaryTypographyProps={{ component: "div" }} // or 'span'
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
            {loading && (
              <ListItem>
                <CircularProgress size={20} />
                <Typography>Chef Chatty is thinking...</Typography>
              </ListItem>
            )}
            <div ref={messagesEndRef} />
          </List>
        </Paper>
      </Container>

      {/* Sticky input form */}
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          sendMessage();
        }}
        sx={{
          position: "sticky",
          bottom: 10,
          bgcolor: "background.paper",
          // borderTop: "1px solid #ccc",
          py: 2,
          zIndex: 1100,
        }}
      >
      <Container maxWidth="sm">
        <Stack
          direction="row"
          // padding={6}
          paddingBottom={6}
          paddingLeft={2}
          paddingRight={2}
          spacing={1}
          mt={2}
        >
          <TextField
            label="How may I help you?"
            value={input}
            onChange={handleInputChange}
            fullWidth
          />
          <Button
            onClick={() => sendMessage()}
            variant="contained"
            color="primary"
          >
            Send
          </Button>
          <Button
            onClick={resetConversation}
            variant="outlined"
            color="secondary"
          >
            Reset
          </Button>
        </Stack>
        </Container>

        {/* The Dialog for showing full-size image */}
        <Dialog
          open={openImagePopup}
          onClose={handleCloseImagePopup}
          onClick={handleCloseImagePopup}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{popupImageTitle}</DialogTitle>
          <DialogContent>
            {popupImageUrl && (
              <Box>
                <img
                  src={popupImageUrl}
                  alt={popupImageTitle}
                  style={{ width: "100%" }}
                />
                <Typography variant="body2" sx={{ fontStyle: "italic", mt: 1 }}>
                  Image source:&nbsp;
                  <Link
                    href={
                      getDomainFromUrl(popupImageUrl).protocol +
                      "//" +
                      getDomainFromUrl(popupImageUrl).hostname
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getDomainFromUrl(popupImageUrl).hostname}
                  </Link>
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
        {/* Enhanced LoginModal with custom onSignup */}
        <AuthDrawer
          open={showDrawer}
          onClose={() => setShowDrawer(false)}
          onLogin={login} // Provided by AuthContext
          onSignup={handleSignup} // Our custom signup -> navigate logic
          onRequestPasswordReset={onRequestPasswordReset}
          onResendConfirmation={onResendConfirmation}
        />
      </Box>
    </Box>
  );
};

export default MealPlanner;
